import './ContactList.scss';
import React, { useMemo, useCallback } from 'react';
import { Star } from '@phosphor-icons/react';
import { useContactsAppStore } from '../../store/contactsAppStore';
import { ContactEntity } from '../../types/entity';
import { formatRelativeTime } from '../../utils/time';

interface ContactListProps {
  windowId: string;
  spaceId: string;
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

// Maps a name to one of 6 avatar color slots (0–5) deterministically.
function getAvatarColorIndex(name: string): number {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  return hash % 6;
}

function ContactRow({ contact, isSelected, onClick, onToggleFavorite }: {
  contact: ContactEntity;
  isSelected: boolean;
  onClick: () => void;
  onToggleFavorite: (e: React.MouseEvent) => void;
}) {
  const sk = contact.skeleton;
  const isFavorite = sk.tier === 'close';
  const lastAt = sk.lastInteraction?.at;
  const nextAt = sk.nextReachOutAt;

  return (
    <button
      className={`contact-list__item${isSelected ? ' contact-list__item--selected' : ''}`}
      onClick={onClick}
    >
      <div
        className="contact-list__avatar"
        data-color={sk.avatarUrl ? undefined : getAvatarColorIndex(contact.name)}
      >
        {sk.avatarUrl ? (
          <img src={sk.avatarUrl} alt={contact.name} className="contact-list__avatar-img" />
        ) : (
          <span className="contact-list__avatar-initials">{getInitials(contact.name)}</span>
        )}
      </div>
      <div className="contact-list__info">
        <div className="contact-list__row1">
          <span className="contact-list__name">{contact.name}</span>
        </div>
        {sk.company && (
          <span className="contact-list__company">{sk.title ? `${sk.title} · ` : ''}{sk.company}</span>
        )}
        <div className="contact-list__row3">
          {lastAt && (
            <span className="contact-list__last">Last: {formatRelativeTime(lastAt)}</span>
          )}
          {nextAt && (
            <span className="contact-list__next">Next: {formatRelativeTime(nextAt)}</span>
          )}
        </div>
      </div>
      <button
        className={`contact-list__star${isFavorite ? ' contact-list__star--active' : ''}`}
        onClick={onToggleFavorite}
        title={isFavorite ? 'Remove from Favorites' : 'Add to Favorites'}
        aria-label={isFavorite ? 'Remove from Favorites' : 'Add to Favorites'}
      >
        <Star size={14} weight={isFavorite ? 'fill' : 'regular'} />
      </button>
    </button>
  );
}

export const ContactList: React.FC<ContactListProps> = ({ windowId, spaceId }) => {
  const store = useContactsAppStore(windowId, spaceId);
  const selectedContactId = store((s) => s.selectedContactId);
  const selectContact = store((s) => s.selectContact);
  const updateContact = store((s) => s.updateContact);
  const getFilteredContacts = store((s) => s.getFilteredContacts);
  const rawContacts = store((s) => s.contacts);
  const currentView = store((s) => s.currentView);
  const filter = store((s) => s.filter);

  const contacts = useMemo(() => getFilteredContacts(), [rawContacts, currentView, filter, getFilteredContacts]);

  const handleToggleFavorite = useCallback((contact: ContactEntity) => (e: React.MouseEvent) => {
    e.stopPropagation();
    const newTier = contact.skeleton.tier === 'close' ? 'regular' : 'close';
    updateContact(contact._id, { tier: newTier });
  }, [updateContact]);

  if (contacts.length === 0) {
    return (
      <div className="contact-list contact-list--empty">
        <p>No contacts found</p>
        <span>Add one or use Discover to find them from Gmail</span>
      </div>
    );
  }

  return (
    <div className="contact-list">
      {contacts.map((contact) => (
        <ContactRow
          key={contact._id}
          contact={contact}
          isSelected={contact._id === selectedContactId}
          onClick={() => selectContact(contact._id)}
          onToggleFavorite={handleToggleFavorite(contact)}
        />
      ))}
    </div>
  );
};
